import { type FC, useCallback } from "react";
import { useDispatch } from "react-redux";

import { ActionButtonIntent, AlertDialogComponent } from "widgets";

import { getSavedItemTypeLabel } from "@/app-utils/string.ts";
import { type SavedItemsListing } from "@/graphql.ts";
import type { SavedItemFragment } from "@/modules/saved-items/graphql/saved-items.gql.ts";
import { deleteFlags } from "@/redux/modules/flag.ts";
import { deleteCoordinateLists } from "@/redux/modules/nuFeature.ts";
import { deleteParents } from "@/redux/modules/parent.ts";
import { deleteGroups } from "@/redux/modules/saved-group.ts";
import { deleteViews } from "@/redux/modules/saved-view.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { ENTITY_TYPE } from "@/settings/settings.ts";

interface DeleteSavedItemDialogProps {
  savedItem: SavedItemFragment;
  itemList: SavedItemsListing; // to know the context, e.g. for the type label because displayName is not fitting here

  onApply: (editedItem: SavedItemFragment) => void;
  onClose: () => void;
}

const DeleteSavedItemDialog: FC<DeleteSavedItemDialogProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();

  const onDelete = useCallback(() => {
    const savedItem = props.savedItem;
    if (savedItem.type === ENTITY_TYPE.VIEW) {
      return dispatch(deleteViews([savedItem.id]));
    } else if (savedItem.type === ENTITY_TYPE.REGION_FLAG) {
      return dispatch(deleteFlags([savedItem.id]));
    } else if (savedItem.type === ENTITY_TYPE.COORD_LIST) {
      return dispatch(deleteCoordinateLists([savedItem.id]));
    } else if (savedItem.type === ENTITY_TYPE.SAMPLE_PARENT) {
      return dispatch(deleteParents([savedItem.id], savedItem.name));
    } else {
      return dispatch(deleteGroups([savedItem.id], savedItem.type));
    }
  }, [props.savedItem]);

  const typeLabel: string = getSavedItemTypeLabel(props.itemList);

  return (
    <AlertDialogComponent
      domTitleProps={{ title: `Delete ${typeLabel}` }}
      mainButton={{
        label: "Delete",
        icon: "delete",
        intent: ActionButtonIntent.Danger,
        onPress: () =>
          onDelete().then(() => {
            props.onApply(props.savedItem);
            props.onClose();
          }),
      }}
      closeFn={props.onClose}
    >
      Are you sure you want to delete {typeLabel} <span style={{ fontWeight: 600 }}>{props.savedItem.name}</span>?
    </AlertDialogComponent>
  );
};

export { DeleteSavedItemDialog };
